import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, formatCurrency, formatDate, getFrequencyLabel, getTontineTypeLabel } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, Users, Wallet, Calendar, Clock,
  TrendingUp, Banknote, CheckCircle, AlertCircle,
  Crown, Loader2, Copy, Share2, Download, Upload,
  FileText, RefreshCw, CreditCard, Bell, Mail,
  QrCode, Link as LinkIcon, UserPlus, Settings, Shield
  , SquarePen, Trash2, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { TontineRiskSummary, MemberRiskBadge, MemberRiskDialog } from '../components/RiskScoring';
import { useLanguage } from '../i18n';
import { handleLocalizedInvalid, clearLocalizedInvalid } from '../lib/localizedValidation';

export const TontineDetailPage = () => {
  const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100', 'all'];
  const CONTRIBUTIONS_ALL_FETCH_CHUNK = 500;
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [tontine, setTontine] = useState(null);
  const [members, setMembers] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContribDialog, setShowContribDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [selectedMemberForRisk, setSelectedMemberForRisk] = useState(null);
  const [membersPage, setMembersPage] = useState(1);
  const [membersPageSize, setMembersPageSize] = useState('10');
  const [contributionsPageSize, setContributionsPageSize] = useState('20');
  const [contributionsOffset, setContributionsOffset] = useState(0);
  const [totalContributions, setTotalContributions] = useState(0);
  const [contributionsLoading, setContributionsLoading] = useState(false);
  const [lastDistributionSummary, setLastDistributionSummary] = useState(null);

  useEffect(() => {
    setContributionsOffset(0);
    setLastDistributionSummary(null);
    loadData();
    // Check for payment callback
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (paymentStatus === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [id, searchParams]);

  useEffect(() => {
    setContributionsOffset(0);
  }, [contributionsPageSize]);

  useEffect(() => {
    if (!id) {
      return;
    }
    loadContributionsPage(contributionsOffset);
  }, [id, contributionsOffset, contributionsPageSize]);

  const loadData = async () => {
    try {
      const [tontineRes, membersRes] = await Promise.all([
        api.getTontine(id),
        api.getTontineMembers(id),
      ]);
      setTontine(tontineRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error loading tontine:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContributionsPage = async (offset) => {
    setContributionsLoading(true);
    try {
      const isAllMode = contributionsPageSize === 'all';

      if (isAllMode) {
        let nextOffset = 0;
        let totalCount = 0;
        let allItems = [];

        do {
          const response = await api.getTontineContributions(id, {
            limit: CONTRIBUTIONS_ALL_FETCH_CHUNK,
            offset: nextOffset,
          });
          const items = response.data || [];
          const totalCountRaw = response.headers?.['x-total-count'];
          if (totalCount === 0 && Number.isFinite(Number(totalCountRaw))) {
            totalCount = Number(totalCountRaw);
          }

          allItems = [...allItems, ...items];
          nextOffset += CONTRIBUTIONS_ALL_FETCH_CHUNK;

          if (items.length === 0) {
            break;
          }
        } while (totalCount === 0 || allItems.length < totalCount);

        setContributions(allItems);
        setTotalContributions(totalCount || allItems.length);
        return;
      }

      const numericPageSize = Number(contributionsPageSize);
      const response = await api.getTontineContributions(id, {
        limit: numericPageSize,
        offset,
      });
      const items = response.data || [];
      const totalCountRaw = response.headers?.['x-total-count'];
      const totalCount = Number.isFinite(Number(totalCountRaw)) ? Number(totalCountRaw) : items.length;
      setContributions(items);
      setTotalContributions(totalCount);
    } catch (error) {
      console.error('Error loading contributions page:', error);
      toast.error(t('tontineDetail.toast.loadError'));
    } finally {
      setContributionsLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.getPaymentStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success(t('tontineDetail.toast.paymentConfirmed'));
        loadData();
        loadContributionsPage(contributionsOffset);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await api.exportContributionsCsv(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contributions_${id.slice(0, 8)}.csv`;
      a.click();
      toast.success(t('tontineDetail.toast.csvDownloaded'));
    } catch (error) {
      toast.error(t('tontineDetail.toast.exportError'));
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await api.exportPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_${id.slice(0, 8)}.pdf`;
      a.click();
      toast.success(t('tontineDetail.toast.pdfDownloaded'));
    } catch (error) {
      toast.error(t('tontineDetail.toast.exportPdfError'));
    }
  };

  const handleExecuteRotation = async () => {
    try {
      const response = await api.executeRotation(id);
      const distribution = Array.isArray(response.data?.distribution) ? response.data.distribution : [];
      if (distribution.length > 0) {
        setLastDistributionSummary({
          totalAmount: Number(response.data?.amount || 0),
          newRound: Number(response.data?.new_round || 0),
          distribution,
        });
      } else {
        setLastDistributionSummary(null);
      }
      toast.success(t('tontineDetail.toast.rotationSuccess', { recipient: response.data.recipient }));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tontineDetail.toast.rotationError'));
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await api.sendReminders(id);
      toast.success(t('tontineDetail.toast.remindersSent', { count: response.data?.sent_count || 0 }));
    } catch (error) {
      toast.error(t('tontineDetail.toast.sendError'));
    }
  };

  const handleSendSmartReminders = async () => {
    try {
      const response = await api.sendSmartReminders(id, false);
      const stats = response.data?.stats || {};
      toast.success(t('tontineDetail.toast.smartRemindersSent', {
        count: response.data?.sent_count || 0,
        highRisk: stats.high_risk_targeted || 0,
        push: stats.push_queued || 0,
        sms: stats.sms_sent || 0,
        email: stats.email_fallback || 0,
      }));
    } catch (error) {
      toast.error(t('tontineDetail.toast.sendError'));
    }
  };

  const handleArchiveTontine = async () => {
    setArchiving(true);
    try {
      await api.deleteTontine(id);
      toast.success('Tontine archivée avec succès');
      navigate('/tontines');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l’archivage');
    } finally {
      setArchiving(false);
    }
  };

  const currentMember = members.find(m => m.user_id === user?.id);
  const isAdminLegacy = currentMember?.role === 'admin';
  const isRestricted = tontine?.visibility_mode === 'restricted';
  const effectiveAccess = tontine?.effective_access || {};
  const permissionSet = new Set(effectiveAccess?.permissions || []);
  const canViewMemberDirectory = typeof effectiveAccess?.can_view_member_directory === 'boolean'
    ? effectiveAccess.can_view_member_directory
    : (!isRestricted || isAdminLegacy);
  const canViewSensitiveMemberData = typeof effectiveAccess?.can_view_sensitive_member_data === 'boolean'
    ? effectiveAccess.can_view_sensitive_member_data
    : (!isRestricted || isAdminLegacy);
  const canRecordContributions = typeof effectiveAccess?.can_record_contributions === 'boolean'
    ? effectiveAccess.can_record_contributions
    : isAdminLegacy;
  const canManageInvites = permissionSet.has('manage_invites') || isAdminLegacy;
  const canManageMembers = permissionSet.has('manage_members') || isAdminLegacy;
  const canSendReminders = permissionSet.has('send_reminders') || isAdminLegacy;
  const canUpdateTontine = permissionSet.has('update_tontine') || isAdminLegacy;
  const canUpdateSettings = Boolean(effectiveAccess?.is_owner) || tontine?.admin_id === user?.id;
  const canArchiveTontine = permissionSet.has('archive_tontine') || isAdminLegacy;
  const canExecuteRotation = permissionSet.has('execute_rotation') || isAdminLegacy;
  const canViewRisk = permissionSet.has('view_risk') || isAdminLegacy;

  useEffect(() => {
    setMembersPage(1);
  }, [id, isRestricted, canViewSensitiveMemberData, user?.id, members.length, membersPageSize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!tontine) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-stone-900 dark:text-stone-50">{t('tontineDetail.notFound')}</h2>
        <Link to="/tontines" className="text-orange-600 mt-2 inline-block">
          {t('tontineDetail.backToTontines')}
        </Link>
      </div>
    );
  }

  const canPayDirectly = tontine?.payment_mode === 'direct' || canRecordContributions;
  const accessRole = String(effectiveAccess?.access_role || (effectiveAccess?.is_owner ? 'owner' : (permissionSet.size > 0 ? 'manager' : 'member')));
  const roleVisuals = {
    owner: {
      label: t('tontineDetail.accessSummary.roleOwner'),
      className: 'text-xs bg-orange-100 text-orange-800 border border-orange-300',
    },
    manager: {
      label: t('tontineDetail.accessSummary.roleManager'),
      className: 'text-xs bg-blue-100 text-blue-800 border border-blue-300',
    },
    collector: {
      label: t('tontineDetail.accessSummary.roleCollector'),
      className: 'text-xs bg-emerald-100 text-emerald-800 border border-emerald-300',
    },
    viewer: {
      label: t('tontineDetail.accessSummary.roleViewer'),
      className: 'text-xs bg-violet-100 text-violet-800 border border-violet-300',
    },
    member: {
      label: t('tontineDetail.accessSummary.roleMember'),
      className: 'text-xs bg-stone-100 text-stone-700 border border-stone-300',
    },
  };
  const currentRoleVisual = roleVisuals[accessRole] || roleVisuals.member;
  const capabilityBadgeClass = accessRole === 'collector'
    ? 'text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
    : accessRole === 'viewer'
      ? 'text-xs bg-violet-100 text-violet-700 hover:bg-violet-100 border border-violet-200'
      : accessRole === 'manager'
        ? 'text-xs bg-blue-100 text-blue-700 hover:bg-blue-100 border border-blue-200'
        : 'text-xs bg-orange-100 text-orange-700 hover:bg-orange-100 border border-orange-200';
  const accessCapabilities = [
    canManageMembers ? t('tontineDetail.accessSummary.capabilities.manageMembers') : null,
    canManageInvites ? t('tontineDetail.accessSummary.capabilities.manageInvites') : null,
    canRecordContributions ? t('tontineDetail.accessSummary.capabilities.recordContributions') : null,
    canUpdateSettings ? t('tontineDetail.accessSummary.capabilities.updateSettings') : null,
    canExecuteRotation ? t('tontineDetail.accessSummary.capabilities.executeRotation') : null,
    canViewSensitiveMemberData ? t('tontineDetail.accessSummary.capabilities.viewSensitiveData') : null,
  ].filter(Boolean);
  const percentLocale = language === 'en' ? 'en-CA' : language === 'wo' ? 'fr-SN' : 'fr-FR';
  const formatPercent = (value) => new Intl.NumberFormat(percentLocale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
  const visibleMembers = isRestricted && !canViewMemberDirectory
    ? members.filter(member => member.user_id === user?.id)
    : members;
  const visibleContributions = isRestricted && !canViewSensitiveMemberData
    ? contributions.filter(contrib => contrib.user_id === user?.id)
    : contributions;
  const isMembersAllMode = membersPageSize === 'all';
  const membersPageSizeValue = isMembersAllMode ? Math.max(visibleMembers.length, 1) : Number(membersPageSize);
  const membersTotalPages = isMembersAllMode ? 1 : Math.max(1, Math.ceil(visibleMembers.length / membersPageSizeValue));
  const safeMembersPage = isMembersAllMode ? 1 : Math.min(membersPage, membersTotalPages);
  const membersStart = isMembersAllMode ? 0 : (safeMembersPage - 1) * membersPageSizeValue;
  const pagedVisibleMembers = isMembersAllMode
    ? visibleMembers
    : visibleMembers.slice(membersStart, membersStart + membersPageSizeValue);

  const isContributionsAllMode = contributionsPageSize === 'all';
  const contributionsPageSizeValue = isContributionsAllMode ? Math.max(totalContributions, 1) : Number(contributionsPageSize);
  const contributionsPage = isContributionsAllMode ? 1 : Math.floor(contributionsOffset / contributionsPageSizeValue) + 1;
  const contributionsTotalPages = isContributionsAllMode ? 1 : Math.max(1, Math.ceil(totalContributions / contributionsPageSizeValue));

  return (
    <div className="space-y-6" data-testid="tontine-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <Link 
            to="/tontines" 
            className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 dark:hover:text-stone-50 mb-4 transition-colors"
            data-testid="back-to-tontines"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">
              {tontine.name}
            </h1>
            {(effectiveAccess?.is_owner || isAdminLegacy) && (
              <Badge
                variant="outline"
                className="text-xs border-orange-200 text-orange-700"
                data-testid="detail-admin-badge"
                title="Vous êtes admin de cette tontine"
              >
                {t('tontine.admin')}
              </Badge>
            )}
            <Badge 
              className={
                tontine.status === 'active' 
                  ? 'bg-green-100 text-green-700 hover:bg-green-100' 
                  : 'bg-stone-100 text-stone-600'
              }
            >
              {tontine.status === 'active' ? t('tontine.statuses.active') : tontine.status}
            </Badge>
          </div>
          <p className="text-stone-600 dark:text-stone-400">
            {getTontineTypeLabel(tontine.type)} • {getFrequencyLabel(tontine.frequency)}
          </p>
          {isRestricted && !canViewSensitiveMemberData && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 text-sm">
              <Shield className="w-4 h-4 text-orange-600" />
              <span>{t('tontineDetail.restrictedInfo')}</span>
            </div>
          )}
          <div className="mt-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-white/80 dark:bg-stone-900/60 px-3 py-3" data-testid="effective-access-summary">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">
              {t('tontineDetail.accessSummary.title')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={currentRoleVisual.className}>
                {currentRoleVisual.label}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {isRestricted ? t('settings.visibilityModes.restricted') : t('settings.visibilityModes.transparent')}
              </Badge>
              {accessCapabilities.map((capability) => (
                <Badge key={capability} className={capabilityBadgeClass}>
                  {capability}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Export buttons */}
          <Button variant="outline" size="sm" className="gap-2" onClick={handleExportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4" />
            CSV
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={handleExportPdf} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4" />
            PDF
          </Button>
          
          {(canManageInvites || canManageMembers || canSendReminders || canUpdateTontine || canUpdateSettings || canArchiveTontine || canExecuteRotation) && (
            <>
              {canManageInvites && (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowInviteDialog(true)} data-testid="invite-btn">
                  <LinkIcon className="w-4 h-4" />
                  {t('tontineDetail.actions.invite')}
                </Button>
              )}
              {canManageMembers && (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowAddMemberDialog(true)} data-testid="add-member-btn">
                  <UserPlus className="w-4 h-4" />
                  {t('tontineDetail.actions.add')}
                </Button>
              )}
              {canSendReminders && (
                <Button variant="outline" size="sm" className="gap-2" onClick={handleSendReminders} data-testid="send-reminders-btn">
                  <Mail className="w-4 h-4" />
                  {t('tontineDetail.actions.reminders')}
                </Button>
              )}
              {canSendReminders && (
                <Button variant="outline" size="sm" className="gap-2" onClick={handleSendSmartReminders} data-testid="send-smart-reminders-btn">
                  <Bell className="w-4 h-4" />
                  {t('tontineDetail.actions.smartReminders')}
                </Button>
              )}
              {canManageMembers && (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowImportDialog(true)} data-testid="import-members-btn">
                  <Upload className="w-4 h-4" />
                  CSV
                </Button>
              )}
              {canUpdateTontine && (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowEditDialog(true)} data-testid="edit-tontine-btn">
                  <SquarePen className="w-4 h-4" />
                  {t('common.edit')}
                </Button>
              )}
              {canUpdateSettings && (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowSettingsDialog(true)} data-testid="settings-btn">
                  <Settings className="w-4 h-4" />
                </Button>
              )}
              {canArchiveTontine && (
                <Button variant="outline" size="sm" className="gap-2 text-red-600 border-red-200" onClick={() => setShowArchiveDialog(true)} data-testid="archive-tontine-btn">
                  <Trash2 className="w-4 h-4" />
                  {t('common.delete')}
                </Button>
              )}
              {canExecuteRotation && (
                <Button variant="outline" size="sm" className="gap-2 text-orange-600 border-orange-200" onClick={handleExecuteRotation} data-testid="execute-rotation-btn">
                  <RefreshCw className="w-4 h-4" />
                  {t('tontineDetail.actions.rotation')}
                </Button>
              )}
            </>
          )}
          
          {/* Pay button - only show if direct payment is allowed OR user is admin */}
          {canPayDirectly && (
            <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="pay-btn">
                  <CreditCard className="w-4 h-4" />
                  {canRecordContributions ? t('contributions.record') : t('contributions.pay')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">
                    {canRecordContributions && tontine?.payment_mode === 'admin_only' 
                      ? t('tontineDetail.paymentDialog.recordContribution')
                      : t('tontineDetail.paymentDialog.chooseMethod')}
                  </DialogTitle>
                  <DialogDescription>
                    {canRecordContributions && tontine?.payment_mode === 'admin_only'
                      ? t('tontineDetail.paymentDialog.recordMemberContribution')
                      : t('tontineDetail.paymentDialog.selectPreferredMethod')}
                  </DialogDescription>
                </DialogHeader>
                <PaymentMethodSelector 
                  tontine={tontine}
                  members={members}
                  canRecordContributions={canRecordContributions}
                  currentUserId={user?.id}
                  onSuccess={() => {
                    setShowPaymentDialog(false);
                    loadData();
                    loadContributionsPage(contributionsOffset);
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
          
          {/* Info message for restricted payment mode */}
          {!canPayDirectly && (
            <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-100 dark:bg-stone-800 px-3 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <span>{t('tontineDetail.followOnlyMessage')}</span>
            </div>
          )}
        </div>
      </div>

      {lastDistributionSummary && (
        <Card className="card-neo border border-orange-200 dark:border-orange-900/50 bg-orange-50/40 dark:bg-orange-900/10">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base text-stone-900 dark:text-stone-50">
                  {t('tontineDetail.distribution.title')}
                </CardTitle>
                <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                  {t('tontineDetail.distribution.totalPot', {
                    amount: formatCurrency(lastDistributionSummary.totalAmount, tontine.currency),
                  })}
                  {lastDistributionSummary.newRound
                    ? ` · ${t('tontineDetail.distribution.newRound', { round: lastDistributionSummary.newRound })}`
                    : ''}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setLastDistributionSummary(null)}
                aria-label={t('tontineDetail.distribution.closeAria')}
                data-testid="distribution-summary-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {lastDistributionSummary.distribution.map((item) => {
              const ratioPercent = Number(item.ratio || 0) * 100;
              return (
                <div key={item.recipient_id} className="space-y-1" data-testid={`distribution-row-${item.recipient_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-stone-800 dark:text-stone-200">{item.recipient_name || t('tontineDetail.distribution.memberFallback')}</span>
                    <span className="text-stone-700 dark:text-stone-300 tabular-nums">
                      {formatCurrency(Number(item.amount || 0), tontine.currency)} · {formatPercent(ratioPercent)}%
                    </span>
                  </div>
                  <Progress value={Math.max(0, Math.min(100, ratioPercent))} className="h-1.5" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('tontineDetail.stats.contribution')}</p>
                <p className="text-lg font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {formatCurrency(tontine.contribution_amount, tontine.currency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('tontineDetail.stats.totalCollected')}</p>
                <p className="text-lg font-bold text-stone-900 dark:text-stone-50 tabular-nums">
                  {formatCurrency(tontine.total_collected, tontine.currency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('tontine.members')}</p>
                <p className="text-lg font-bold text-stone-900 dark:text-stone-50">
                  {tontine.current_members}/{tontine.max_members}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">{t('tontineDetail.stats.currentRound')}</p>
                <p className="text-lg font-bold text-stone-900 dark:text-stone-50">
                  {tontine.current_round}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="members" className="space-y-4">
        <TabsList className="w-full overflow-x-auto whitespace-nowrap">
          <TabsTrigger value="members" data-testid="tab-members">{t('tontine.members')}</TabsTrigger>
          <TabsTrigger value="contributions" data-testid="tab-contributions">{t('tontineDetail.tabs.contributions')}</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">{t('tontine.rules')}</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <Card className="card-neo">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-display text-lg">
                  {t('tontineDetail.membersTitle', { count: visibleMembers.length })}
                </CardTitle>
                <Progress 
                  value={(tontine.current_members / tontine.max_members) * 100} 
                  className="w-32 h-2"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pagedVisibleMembers.map((member) => (
                  <div 
                    key={member.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-xl bg-stone-50 dark:bg-stone-800/50"
                    data-testid={`member-${member.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300">
                            {member.user_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        {member.role === 'admin' && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                            <Crown className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 dark:text-stone-50">
                          {member.user_name}
                        </p>
                        <p className="text-sm text-stone-500">
                          {t('tontineDetail.positionLabel', { position: member.position })}
                        </p>
                      </div>
                    </div>
                    <div className="w-full sm:w-auto text-left sm:text-right">
                      {isRestricted && !canViewSensitiveMemberData && member.user_id !== user?.id ? (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <Badge variant="outline" className="text-stone-600 border-stone-300">
                            <Shield className="w-3 h-3 mr-1 text-orange-600" />
                            {t('tontineDetail.masked')}
                          </Badge>
                        </div>
                      ) : (
                        <p className="font-semibold text-stone-900 dark:text-stone-50 tabular-nums">
                          {formatCurrency(member.total_contributed, tontine.currency)}
                        </p>
                      )}
                      <div className="flex items-center gap-1 justify-end">
                        {member.has_received_payout ? (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {t('tontineDetail.paid')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-stone-500">
                            <Clock className="w-3 h-3 mr-1" />
                            {t('tontine.statuses.pending')}
                          </Badge>
                        )}
                        {/* Risk Score Badge (admin only) */}
                        {canViewRisk && member.role !== 'admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setSelectedMemberForRisk(member);
                              setShowRiskDialog(true);
                            }}
                            title={t('tontineDetail.viewRiskScore')}
                          >
                            <Shield className="w-4 h-4 text-orange-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {visibleMembers.length > 0 && (
                <div className="mt-4 flex items-center justify-between gap-2" data-testid="members-pagination">
                  <p className="text-xs text-stone-500" data-testid="members-page-indicator">
                    {t('common.page')} {safeMembersPage} / {membersTotalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Select value={membersPageSize} onValueChange={setMembersPageSize}>
                      <SelectTrigger className="w-[120px] h-8" data-testid="members-page-size-select">
                        <SelectValue placeholder={t('common.pageSize')} />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option === 'all' ? t('common.all') : option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMembersPage((prev) => Math.max(1, prev - 1))}
                      disabled={safeMembersPage === 1 || isMembersAllMode}
                      data-testid="members-prev-btn"
                    >
                      {t('common.previous')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMembersPage((prev) => Math.min(membersTotalPages, prev + 1))}
                      disabled={safeMembersPage >= membersTotalPages || isMembersAllMode}
                      data-testid="members-next-btn"
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Risk Summary Card (admin only) */}
          {canViewRisk && (
            <div className="mt-4">
              <TontineRiskSummary tontineId={id} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="contributions">
          <Card className="card-neo">
            <CardHeader>
              <CardTitle className="font-display text-lg">
                {t('tontineDetail.contributionHistory')}
              </CardTitle>
              {isRestricted && !canViewSensitiveMemberData && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 text-sm">
                  <Shield className="w-4 h-4 text-orange-600" />
                  <span>{t('tontineDetail.restrictedContributionsInfo')}</span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {visibleContributions.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">{t('tontineDetail.noContributions')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {visibleContributions.map((contrib) => (
                    <div 
                      key={contrib.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-xl bg-stone-50 dark:bg-stone-800/50"
                      data-testid={`contribution-${contrib.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                          <Banknote className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900 dark:text-stone-50">
                            {contrib.user_name}
                          </p>
                          <p className="text-sm text-stone-500">
                            {formatDate(contrib.created_at)} • {t('tontineDetail.roundLabel', { round: contrib.round_number })}
                          </p>
                        </div>
                      </div>
                      <div className="w-full sm:w-auto text-left sm:text-right">
                        <p className="font-semibold text-green-600 tabular-nums">
                          +{formatCurrency(contrib.amount, tontine.currency)}
                        </p>
                        <p className="text-xs text-stone-500 capitalize">
                          {contrib.payment_method.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalContributions > 0 && (
                <div className="mt-4 flex items-center justify-between gap-2" data-testid="contributions-pagination">
                  <p className="text-xs text-stone-500" data-testid="contributions-page-indicator">
                    {t('common.page')} {contributionsPage} / {contributionsTotalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Select value={contributionsPageSize} onValueChange={setContributionsPageSize}>
                      <SelectTrigger className="w-[120px] h-8" data-testid="contributions-page-size-select">
                        <SelectValue placeholder={t('common.pageSize')} />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option === 'all' ? t('common.all') : option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setContributionsOffset((prev) => Math.max(0, prev - contributionsPageSizeValue))}
                      disabled={contributionsLoading || contributionsOffset === 0 || isContributionsAllMode}
                      data-testid="contributions-prev-btn"
                    >
                      {t('common.previous')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setContributionsOffset((prev) => prev + contributionsPageSizeValue)}
                      disabled={contributionsLoading || contributionsPage >= contributionsTotalPages || isContributionsAllMode}
                      data-testid="contributions-next-btn"
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                </div>
              )}

              {contributionsLoading && (
                <div className="mt-3 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card className="card-neo">
            <CardHeader>
              <CardTitle className="font-display text-lg">
                {t('tontineDetail.rulesTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tontine.rules ? (
                <div className="prose prose-stone dark:prose-invert max-w-none">
                  <p className="whitespace-pre-wrap">{tontine.rules}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-stone-500">{t('tontineDetail.noRules')}</p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-stone-200 dark:border-stone-800">
                <h4 className="font-semibold text-stone-900 dark:text-stone-50 mb-3">{t('tontineDetail.infoTitle')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">{t('tontineDetail.createdOn')}</p>
                    <p className="font-medium text-stone-900 dark:text-stone-50">
                      {formatDate(tontine.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-stone-500">{t('tontine.currency')}</p>
                    <p className="font-medium text-stone-900 dark:text-stone-50">
                      {tontine.currency === 'XOF' ? t('tontineDetail.currencyXof') : t('tontineDetail.currencyCad')}
                    </p>
                  </div>
                  {tontine.next_payout_date && (
                    <div>
                      <p className="text-stone-500">{t('tontineDetail.nextPayout')}</p>
                      <p className="font-medium text-stone-900 dark:text-stone-50">
                        {formatDate(tontine.next_payout_date)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t('tontineDetail.dialogs.import.title')}</DialogTitle>
            <DialogDescription>
              {t('tontineDetail.dialogs.import.description')}
            </DialogDescription>
          </DialogHeader>
          <ImportMembersForm 
            tontineId={id}
            onSuccess={() => {
              setShowImportDialog(false);
              loadData();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t('tontineDetail.dialogs.invite.title')}</DialogTitle>
            <DialogDescription>
              {t('tontineDetail.dialogs.invite.description')}
            </DialogDescription>
          </DialogHeader>
          <InviteForm tontineId={id} />
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t('tontineDetail.dialogs.addMember.title')}</DialogTitle>
            <DialogDescription>
              {t('tontineDetail.dialogs.addMember.description')}
            </DialogDescription>
          </DialogHeader>
          <AddMemberForm 
            tontineId={id}
            currentMemberCount={tontine?.current_members || 0}
            onSuccess={() => {
              setShowAddMemberDialog(false);
              loadData();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t('tontineDetail.dialogs.settings.title')}</DialogTitle>
            <DialogDescription>
              {t('tontineDetail.dialogs.settings.description')}
            </DialogDescription>
          </DialogHeader>
          <SettingsForm 
            tontineId={id}
            onSuccess={(updatedSettings) => {
              setShowSettingsDialog(false);
              if (updatedSettings) {
                setTontine((prev) => prev ? {
                  ...prev,
                  payment_mode: updatedSettings.payment_mode,
                  visibility_mode: updatedSettings.visibility_mode,
                } : prev);
              }
              loadData();
              toast.success(t('settings.saved'));
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Tontine Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Modifier la tontine</DialogTitle>
            <DialogDescription>
              Mettez à jour les informations principales de votre tontine.
            </DialogDescription>
          </DialogHeader>
          <EditTontineForm
            tontine={tontine}
            onSuccess={() => {
              setShowEditDialog(false);
              loadData();
              toast.success(t('settings.saved'));
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Archive Tontine Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Supprimer la tontine ?</DialogTitle>
            <DialogDescription>
              Cette action archive la tontine. Elle disparaîtra de la liste active.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)} data-testid="archive-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleArchiveTontine} disabled={archiving} data-testid="archive-confirm-btn">
              {archiving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('common.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Risk Score Dialog */}
      <MemberRiskDialog
        tontineId={id}
        memberId={selectedMemberForRisk?.id}
        memberName={selectedMemberForRisk?.user_name}
        isOpen={showRiskDialog}
        onClose={() => {
          setShowRiskDialog(false);
          setSelectedMemberForRisk(null);
        }}
      />
    </div>
  );
};

const PaymentMethodSelector = ({ tontine, members = [], canRecordContributions = false, currentUserId, onSuccess }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [wavePayment, setWavePayment] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(currentUserId);
  const [contributionAmount, setContributionAmount] = useState(String(tontine?.contribution_amount || ''));
  const isFlexibleType = tontine?.type === 'flex_asca';
  
  // Filter members for admin selection (only non-admin members in admin_only mode)
  const selectableMembers = canRecordContributions && tontine?.payment_mode === 'admin_only' 
    ? members.filter(m => m.status === 'active')
    : [];

  useEffect(() => {
    setContributionAmount(String(tontine?.contribution_amount || ''));
  }, [tontine?.id, tontine?.contribution_amount]);

  const handleStripePayment = async () => {
    const resolvedAmount = isFlexibleType
      ? parseFloat(contributionAmount)
      : Number(tontine.contribution_amount);

    if (!Number.isFinite(resolvedAmount) || resolvedAmount <= 0) {
      toast.error('Veuillez saisir un montant valide supérieur à 0');
      return;
    }

    if (tontine.currency !== 'CAD') {
      toast.error(t('tontineDetail.payment.stripeCadOnly'));
      return;
    }
    setLoading(true);
    try {
      const response = await api.initiateStripePayment(tontine.id, window.location.origin, resolvedAmount);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tontineDetail.payment.error'));
      setLoading(false);
    }
  };

  const handleWavePayment = async () => {
    const resolvedAmount = isFlexibleType
      ? parseFloat(contributionAmount)
      : Number(tontine.contribution_amount);

    if (!Number.isFinite(resolvedAmount) || resolvedAmount <= 0) {
      toast.error('Veuillez saisir un montant valide supérieur à 0');
      return;
    }

    if (tontine.currency !== 'XOF') {
      toast.error(t('tontineDetail.payment.waveXofOnly'));
      return;
    }
    setLoading(true);
    try {
      const response = await api.initiateWavePayment(tontine.id, window.location.origin, resolvedAmount);
      setWavePayment(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tontineDetail.payment.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCashPayment = async () => {
    const resolvedAmount = isFlexibleType
      ? parseFloat(contributionAmount)
      : Number(tontine.contribution_amount);

    if (!Number.isFinite(resolvedAmount) || resolvedAmount <= 0) {
      toast.error('Veuillez saisir un montant valide supérieur à 0');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tontine_id: tontine.id,
        amount: resolvedAmount,
        payment_method: 'cash'
      };
      
      // If admin is recording for another member
      if (canRecordContributions && selectedMemberId && selectedMemberId !== currentUserId) {
        payload.for_user_id = selectedMemberId;
      }
      
      await api.createContribution(payload);
      
      const memberName = selectableMembers.find(m => m.user_id === selectedMemberId)?.user_name || t('tontineDetail.payment.you');
      toast.success(t('tontineDetail.payment.recordedFor', { memberName }));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tontineDetail.payment.recordError'));
    } finally {
      setLoading(false);
    }
  };

  if (wavePayment) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
          <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">{t('tontineDetail.payment.waveInstructions')}</p>
          <p className="text-stone-700 dark:text-stone-300">{wavePayment.instructions}</p>
        </div>
        <div className="bg-stone-100 dark:bg-stone-800 p-4 rounded-xl text-center">
          <p className="text-sm text-stone-500 mb-1">{t('tontineDetail.payment.reference')}</p>
          <p className="text-xl font-mono font-bold text-stone-900 dark:text-stone-50">
            {wavePayment.payment_ref}
          </p>
        </div>
        <Button variant="outline" className="w-full" onClick={() => setWavePayment(null)}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Member selection for admin in admin_only mode */}
      {canRecordContributions && tontine?.payment_mode === 'admin_only' && selectableMembers.length > 0 && (
        <div className="space-y-2 mb-4">
          <Label>{t('contributions.recordFor')}</Label>
          <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
            <SelectTrigger data-testid="contribution-member-select">
              <SelectValue placeholder={t('contributions.selectMember')} />
            </SelectTrigger>
            <SelectContent>
              {selectableMembers.map(member => (
                <SelectItem key={member.user_id} value={member.user_id}>
                  <div className="flex items-center gap-2">
                    <span>{member.user_name}</span>
                    <span className="text-xs text-stone-500">#{member.position}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="bg-stone-50 dark:bg-stone-800/50 p-4 rounded-xl text-center mb-4 space-y-3">
        <p className="text-sm text-stone-500">{t('tontineDetail.payment.amountToRecord')}</p>
        {isFlexibleType ? (
          <div className="space-y-2">
            <Input
              type="number"
              min="1"
              step="1"
              value={contributionAmount}
              onChange={(e) => setContributionAmount(e.target.value)}
              placeholder={String(tontine.contribution_amount || '')}
              data-testid="flex-contribution-amount-input"
            />
            <p className="text-xs text-stone-500">{t('tontine.types.flex_asca')}</p>
          </div>
        ) : (
          <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 tabular-nums">
            {formatCurrency(tontine.contribution_amount, tontine.currency)}
          </p>
        )}
      </div>

      {/* Show payment methods only for direct payments (not admin_only mode) */}
      {tontine?.payment_mode === 'direct' && (
        <>
          {tontine.currency === 'XOF' ? (
            <>
              <Button 
                className="w-full justify-start gap-3 h-14" 
                variant="outline"
                onClick={handleWavePayment}
                disabled={loading}
              >
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">W</div>
                <div className="text-left">
                  <p className="font-medium">{t('contributions.paymentMethods.wave')}</p>
                  <p className="text-xs text-stone-500">{t('tontineDetail.payment.mobileMoney')}</p>
                </div>
              </Button>
              <Button 
                className="w-full justify-start gap-3 h-14" 
                variant="outline"
                onClick={handleWavePayment}
                disabled={loading}
              >
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">O</div>
                <div className="text-left">
                  <p className="font-medium">{t('contributions.paymentMethods.orangeMoney')}</p>
                  <p className="text-xs text-stone-500">{t('tontineDetail.payment.mobileMoney')}</p>
                </div>
              </Button>
            </>
          ) : (
            <Button 
              className="w-full justify-start gap-3 h-14" 
              variant="outline"
              onClick={handleStripePayment}
              disabled={loading}
            >
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="font-medium">{t('contributions.paymentMethods.stripe')}</p>
                <p className="text-xs text-stone-500">{t('tontineDetail.payment.cardBrands')}</p>
              </div>
              {loading && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
            </Button>
          )}
        </>
      )}

      <Button 
        className="w-full justify-start gap-3 h-14" 
        variant="outline"
        onClick={handleCashPayment}
        disabled={loading}
        data-testid="cash-payment-btn"
      >
        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
          <Banknote className="w-4 h-4 text-white" />
        </div>
        <div className="text-left">
          <p className="font-medium">{canRecordContributions && tontine?.payment_mode === 'admin_only' ? t('tontineDetail.payment.recordCash') : t('contributions.paymentMethods.cash')}</p>
          <p className="text-xs text-stone-500">{canRecordContributions && tontine?.payment_mode === 'admin_only' ? t('tontineDetail.payment.manualContribution') : t('tontineDetail.payment.manualPayment')}</p>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
      </Button>
    </div>
  );
};

const ImportMembersForm = ({ tontineId, onSuccess }) => {
  const { t } = useLanguage();
  const [csvContent, setCsvContent] = useState('');
  const [mode, setMode] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvContent(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.getCsvTemplate(tontineId, mode);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${mode}.csv`;
      a.click();
    } catch (error) {
      toast.error(t('tontineDetail.import.downloadError'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!csvContent.trim()) {
      toast.error(t('tontineDetail.import.selectCsvError'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.importMembers(tontineId, csvContent, mode);
      setResult(response.data);
      toast.success(response.data.message);
      if (response.data.imported > 0) {
        setTimeout(() => onSuccess(), 2000);
      }
    } catch (error) {
      const errors = error.response?.data?.detail?.errors || [error.response?.data?.detail || t('tontineDetail.import.importError')];
      toast.error(errors[0]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
      onInputCapture={clearLocalizedInvalid}
      onChangeCapture={clearLocalizedInvalid}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label>{t('tontineDetail.import.mode')}</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">{t('tontineDetail.import.modeBasic')}</SelectItem>
            <SelectItem value="full">{t('tontineDetail.import.modeFull')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" />
          {t('tontineDetail.import.downloadTemplate')}
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="csv-file">{t('tontineDetail.import.csvFile')}</Label>
        <Input
          id="csv-file"
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="cursor-pointer"
        />
      </div>

      {csvContent && (
        <div className="bg-stone-50 dark:bg-stone-800/50 p-3 rounded-lg max-h-32 overflow-auto">
          <pre className="text-xs text-stone-600 dark:text-stone-400">{csvContent.slice(0, 500)}...</pre>
        </div>
      )}

      {result && (
        <div className={`p-3 rounded-lg ${result.errors?.length ? 'bg-yellow-50' : 'bg-green-50'}`}>
          <p className="font-medium text-sm">
            {t('tontineDetail.import.resultSummary', { imported: result.imported, skipped: result.skipped })}
          </p>
          {result.errors?.length > 0 && (
            <ul className="text-xs text-yellow-700 mt-1">
              {result.errors.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      <Button type="submit" className="w-full btn-accent" disabled={loading}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
        {t('tontineDetail.import.submit')}
      </Button>
    </form>
  );
};

const InviteForm = ({ tontineId }) => {
  const { t } = useLanguage();
  const [invite, setInvite] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadInvite();
  }, [tontineId]);

  const loadInvite = async () => {
    try {
      const response = await api.getInvite(tontineId);
      setInvite(response.data);
      const auditResponse = await api.getInviteAudit(tontineId, 10);
      setAuditEvents(auditResponse.data || []);
    } catch (error) {
      // Create new invite if none exists
      try {
        const response = await api.createInvite(tontineId, { max_uses: 100, expires_in_days: 30 });
        setInvite(response.data);
        const auditResponse = await api.getInviteAudit(tontineId, 10);
        setAuditEvents(auditResponse.data || []);
      } catch (e) {
        console.error('Error creating invite:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (invite?.invite_link) {
      await navigator.clipboard.writeText(invite.invite_link);
      setCopied(true);
      toast.success(t('invite.copied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendInvites = async () => {
    const emailList = emails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    if (emailList.length === 0) {
      toast.error(t('tontineDetail.invite.enterOneEmail'));
      return;
    }
    setSending(true);
    try {
      await api.createInvite(tontineId, { emails: emailList, max_uses: emailList.length + 5, expires_in_days: 7 });
      toast.success(t('tontineDetail.invite.sentCount', { count: emailList.length }));
      setEmails('');
      await loadInvite();
    } catch (error) {
      toast.error(t('tontineDetail.toast.sendError'));
    } finally {
      setSending(false);
    }
  };

  const getAuditEventLabel = (eventType) => {
    if (!eventType) return '-';
    const key = `invite.auditEvents.${eventType}`;
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }
    return eventType.replace(/_/g, ' ');
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Link */}
      <div className="space-y-2">
        <Label>{t('invite.link')}</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={invite?.invite_link || ''} readOnly className="text-sm" data-testid="invite-link-input" />
          <Button variant="outline" size="icon" onClick={copyLink} className="self-end sm:self-auto shrink-0" data-testid="invite-copy-btn">
            {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* QR Code */}
      {invite?.qr_code_url && (
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <img src={invite.qr_code_url} alt={t('invite.qrCode')} className="w-32 h-32 sm:w-40 sm:h-40" />
          </div>
        </div>
      )}

      {/* Code */}
      <div className="bg-stone-100 dark:bg-stone-800 p-4 rounded-xl text-center">
        <p className="text-xs text-stone-500 mb-1">{t('invite.code')}</p>
        <p className="text-2xl font-mono font-bold text-stone-900 dark:text-stone-50">
          {invite?.invite_code}
        </p>
        <p className="text-xs text-stone-500 mt-2">
          {t('invite.expires')} : {invite?.expires_at ? formatDate(invite.expires_at) : '-'}
        </p>
        <p className="text-xs text-stone-500 mt-1">
          {t('invite.usesLabel')} : {invite?.current_uses ?? 0}/{invite?.max_uses ?? 0}
        </p>
      </div>

      {auditEvents.length > 0 && (
        <div className="space-y-2 pt-4 border-t">
          <Label>{t('invite.auditTitle')}</Label>
          <div className="max-h-48 overflow-auto space-y-2">
            {auditEvents.map((event) => (
              <div key={event.id} className="text-xs p-2 rounded-lg bg-stone-100 dark:bg-stone-800">
                <p className="font-medium" data-testid="invite-audit-event-type">{getAuditEventLabel(event.event_type)}</p>
                <p className="text-stone-500">{formatDate(event.created_at)}</p>
                {event.details?.reason && <p className="text-stone-600">{t('invite.reasonLabel')}: {event.details.reason}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Email invites */}
      <div className="space-y-2 pt-4 border-t">
        <Label>{t('invite.email')}</Label>
        <Textarea
          placeholder={t('tontineDetail.invite.emailPlaceholder')}
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          rows={3}
          data-testid="invite-emails-input"
        />
        <Button className="w-full" onClick={sendInvites} disabled={sending} data-testid="invite-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
          {t('tontineDetail.invite.sendInvites')}
        </Button>
      </div>
    </div>
  );
};

const AddMemberForm = ({ tontineId, currentMemberCount, onSuccess }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    position: '',
    send_invite: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.addMember(tontineId, {
        ...formData,
        position: formData.position ? parseInt(formData.position) : null
      });
      toast.success(t('tontineDetail.addMember.addedSuccess'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tontineDetail.addMember.addError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
      onInputCapture={clearLocalizedInvalid}
      onChangeCapture={clearLocalizedInvalid}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="full_name">{t('auth.fullName')} *</Label>
        <Input
          id="full_name"
          placeholder={t('authPage.namePlaceholder')}
          value={formData.full_name}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          placeholder="amadou@email.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">{t('auth.phone')}</Label>
        <Input
          id="phone"
          placeholder="+221 77 123 45 67"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="position">{t('tontineDetail.addMember.rotationPosition')}</Label>
        <Input
          id="position"
          type="number"
          placeholder={`${currentMemberCount + 1}`}
          value={formData.position}
          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
          min="1"
        />
        <p className="text-xs text-stone-500">{t('tontineDetail.addMember.positionHelp')}</p>
      </div>

      <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
        <div>
          <p className="font-medium text-sm">{t('tontineDetail.addMember.sendInvite')}</p>
          <p className="text-xs text-stone-500">{t('tontineDetail.addMember.sendInviteHelp')}</p>
        </div>
        <Switch
          checked={formData.send_invite}
          onCheckedChange={(checked) => setFormData({ ...formData, send_invite: checked })}
        />
      </div>

      <Button type="submit" className="w-full btn-accent" disabled={loading}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
        {t('tontineDetail.dialogs.addMember.title')}
      </Button>
    </form>
  );
};

const SettingsForm = ({ tontineId, onSuccess }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    reminder_days: [7, 3, 1],
    smart_reminders_enabled: false,
    smart_reminder_urgent_days: [0],
    auto_rotation: true,
    penalty_enabled: false,
    penalty_rate: 0.05,
    grace_period_days: 3,
    payment_mode: 'admin_only',
    visibility_mode: 'restricted',
    risk_excellent_min: 90,
    risk_good_min: 70,
    risk_moderate_min: 50,
    risk_high_min: 30,
    risk_high_risk_cutoff: 50,
    risk_top_members_min_score: 50,
    risk_top_members_count: 3,
    risk_high_risk_count: 5,
  });

  const normalizeVisibilityMode = (mode) => {
    if (mode === 'transparent' || mode === 'unrestricted' || mode === 'no_restriction' || mode === 'non_restriction') {
      return 'transparent';
    }
    return 'restricted';
  };

  useEffect(() => {
    loadSettings();
  }, [tontineId]);

  const loadSettings = async () => {
    try {
      const response = await api.getTontineSettings(tontineId);
      const nextSettings = {
        ...settings,
        ...response.data,
        visibility_mode: normalizeVisibilityMode(response.data?.visibility_mode),
      };
      setSettings(nextSettings);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...settings,
        visibility_mode: normalizeVisibilityMode(settings.visibility_mode),
        smart_reminders_enabled: Boolean(settings.smart_reminders_enabled),
        smart_reminder_urgent_days: [...new Set((settings.smart_reminder_urgent_days || [])
          .map((day) => Number(day))
          .filter((day) => Number.isFinite(day) && day >= 0 && day <= 30))].sort((a, b) => b - a),
        risk_excellent_min: Math.max(0, Math.min(100, Number(settings.risk_excellent_min) || 0)),
        risk_good_min: Math.max(0, Math.min(100, Number(settings.risk_good_min) || 0)),
        risk_moderate_min: Math.max(0, Math.min(100, Number(settings.risk_moderate_min) || 0)),
        risk_high_min: Math.max(0, Math.min(100, Number(settings.risk_high_min) || 0)),
        risk_high_risk_cutoff: Math.max(0, Math.min(100, Number(settings.risk_high_risk_cutoff) || 0)),
        risk_top_members_min_score: Math.max(0, Math.min(100, Number(settings.risk_top_members_min_score) || 0)),
        risk_top_members_count: Math.max(1, Math.min(20, Number(settings.risk_top_members_count) || 1)),
        risk_high_risk_count: Math.max(1, Math.min(20, Number(settings.risk_high_risk_count) || 1)),
      };
      await api.updateTontineSettings(tontineId, payload);
      onSuccess(payload);
    } catch (error) {
      toast.error(t('tontineDetail.settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
      onInputCapture={clearLocalizedInvalid}
      onChangeCapture={clearLocalizedInvalid}
      className="space-y-6"
    >
      {/* Mode de paiement */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('settings.paymentMode')}</Label>
        <div className="space-y-2">
          <div 
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
              settings.payment_mode === 'admin_only' 
                ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500' 
                : 'bg-stone-50 dark:bg-stone-800/50 border-2 border-transparent'
            }`}
            onClick={() => setSettings({ ...settings, payment_mode: 'admin_only' })}
          >
            <div>
              <p className="font-medium text-sm">{t('settings.paymentModes.adminOnly')}</p>
              <p className="text-xs text-stone-500">{t('settings.paymentModes.adminOnlyDesc')}</p>
            </div>
            {settings.payment_mode === 'admin_only' && (
              <CheckCircle className="w-5 h-5 text-orange-600" />
            )}
          </div>
          <div 
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
              settings.payment_mode === 'direct' 
                ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500' 
                : 'bg-stone-50 dark:bg-stone-800/50 border-2 border-transparent'
            }`}
            onClick={() => setSettings({ ...settings, payment_mode: 'direct' })}
          >
            <div>
              <p className="font-medium text-sm">{t('settings.paymentModes.direct')}</p>
              <p className="text-xs text-stone-500">{t('settings.paymentModes.directDesc')}</p>
            </div>
            {settings.payment_mode === 'direct' && (
              <CheckCircle className="w-5 h-5 text-orange-600" />
            )}
          </div>
        </div>
      </div>

      {/* Mode de visibilité */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('settings.visibilityMode')}</Label>
        <div className="space-y-2">
          <div 
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
              settings.visibility_mode === 'restricted' 
                ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500' 
                : 'bg-stone-50 dark:bg-stone-800/50 border-2 border-transparent'
            }`}
            onClick={() => setSettings({ ...settings, visibility_mode: 'restricted' })}
          >
            <div>
              <p className="font-medium text-sm">{t('settings.visibilityModes.restricted')}</p>
              <p className="text-xs text-stone-500">{t('settings.visibilityModes.restrictedDesc')}</p>
            </div>
            {settings.visibility_mode === 'restricted' && (
              <CheckCircle className="w-5 h-5 text-orange-600" />
            )}
          </div>
          <div 
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
              settings.visibility_mode === 'transparent' 
                ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500' 
                : 'bg-stone-50 dark:bg-stone-800/50 border-2 border-transparent'
            }`}
            onClick={() => setSettings({ ...settings, visibility_mode: 'transparent' })}
          >
            <div>
              <p className="font-medium text-sm">{t('settings.visibilityModes.transparent')}</p>
              <p className="text-xs text-stone-500">{t('settings.visibilityModes.transparentDesc')}</p>
            </div>
            {settings.visibility_mode === 'transparent' && (
              <CheckCircle className="w-5 h-5 text-orange-600" />
            )}
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <p className="text-xs text-stone-500 font-medium uppercase tracking-wider mb-4">{t('settings.automation')}</p>
      </div>

      {/* Rappels automatiques */}
      <div className="space-y-3">
        <Label>{t('settings.reminders')}</Label>
        <p className="text-xs text-stone-500">
          {t('tontineDetail.settings.remindersHelp')}
        </p>
        <div className="flex flex-wrap gap-2">
          {[1, 3, 7, 14].map(day => (
            <Button
              key={day}
              type="button"
              variant={settings.reminder_days.includes(day) ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (settings.reminder_days.includes(day)) {
                  setSettings({ ...settings, reminder_days: settings.reminder_days.filter(d => d !== day) });
                } else {
                  setSettings({ ...settings, reminder_days: [...settings.reminder_days, day].sort((a, b) => b - a) });
                }
              }}
            >
              {t('tontineDetail.settings.reminderDay', { day })}
            </Button>
          ))}
        </div>
      </div>

      {/* Rotation automatique */}
      <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
        <div>
          <p className="font-medium text-sm">{t('settings.autoRotation')}</p>
          <p className="text-xs text-stone-500">{t('tontineDetail.settings.autoRotationHelp')}</p>
        </div>
        <Switch
          checked={settings.auto_rotation}
          onCheckedChange={(checked) => setSettings({ ...settings, auto_rotation: checked })}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800/50 rounded-lg" data-testid="smart-reminders-toggle-row">
          <div>
            <p className="font-medium text-sm">{t('tontineDetail.settings.smartReminders')}</p>
            <p className="text-xs text-stone-500">{t('tontineDetail.settings.smartRemindersHelp')}</p>
          </div>
          <Switch
            checked={settings.smart_reminders_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, smart_reminders_enabled: checked })}
          />
        </div>

        {settings.smart_reminders_enabled && (
          <div className="pl-4 space-y-2" data-testid="smart-reminders-urgent-days">
            <Label>{t('tontineDetail.settings.smartUrgentDays')}</Label>
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3].map((day) => (
                <Button
                  key={day}
                  type="button"
                  variant={settings.smart_reminder_urgent_days.includes(day) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    if (settings.smart_reminder_urgent_days.includes(day)) {
                      setSettings({
                        ...settings,
                        smart_reminder_urgent_days: settings.smart_reminder_urgent_days.filter((d) => d !== day),
                      });
                    } else {
                      setSettings({
                        ...settings,
                        smart_reminder_urgent_days: [...settings.smart_reminder_urgent_days, day].sort((a, b) => b - a),
                      });
                    }
                  }}
                >
                  {t('tontineDetail.settings.reminderDay', { day })}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pénalités */}
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
          <div>
            <p className="font-medium text-sm">{t('tontineDetail.settings.latePenalties')}</p>
            <p className="text-xs text-stone-500">{t('tontineDetail.settings.latePenaltiesHelp')}</p>
          </div>
          <Switch
            checked={settings.penalty_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, penalty_enabled: checked })}
          />
        </div>

        {settings.penalty_enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-4">
            <div className="space-y-2">
              <Label>{t('tontineDetail.settings.penaltyRatePercent')}</Label>
              <Input
                type="number"
                value={settings.penalty_rate * 100}
                onChange={(e) => setSettings({ ...settings, penalty_rate: parseFloat(e.target.value) / 100 })}
                min="0"
                max="50"
                step="1"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.gracePeriod')}</Label>
              <Input
                type="number"
                value={settings.grace_period_days}
                onChange={(e) => setSettings({ ...settings, grace_period_days: parseInt(e.target.value) })}
                min="0"
                max="30"
              />
            </div>
          </div>
        )}
      </div>

      <div className="border-t pt-4" data-testid="risk-settings-section">
        <p className="text-xs text-stone-500 font-medium uppercase tracking-wider mb-4">{t('tontineDetail.settings.riskSectionTitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.riskExcellentMin')}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={settings.risk_excellent_min}
            onChange={(e) => setSettings({ ...settings, risk_excellent_min: parseInt(e.target.value || '0', 10) })}
            data-testid="risk-excellent-min-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.riskGoodMin')}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={settings.risk_good_min}
            onChange={(e) => setSettings({ ...settings, risk_good_min: parseInt(e.target.value || '0', 10) })}
            data-testid="risk-good-min-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.riskModerateMin')}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={settings.risk_moderate_min}
            onChange={(e) => setSettings({ ...settings, risk_moderate_min: parseInt(e.target.value || '0', 10) })}
            data-testid="risk-moderate-min-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.riskHighMin')}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={settings.risk_high_min}
            onChange={(e) => setSettings({ ...settings, risk_high_min: parseInt(e.target.value || '0', 10) })}
            data-testid="risk-high-min-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.highRiskCutoff')}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={settings.risk_high_risk_cutoff}
            onChange={(e) => setSettings({ ...settings, risk_high_risk_cutoff: parseInt(e.target.value || '0', 10) })}
            data-testid="risk-high-cutoff-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.topMembersMinScore')}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={settings.risk_top_members_min_score}
            onChange={(e) => setSettings({ ...settings, risk_top_members_min_score: parseInt(e.target.value || '0', 10) })}
            data-testid="risk-top-members-min-score-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.topMembersCount')}</Label>
          <Input
            type="number"
            min="1"
            max="20"
            value={settings.risk_top_members_count}
            onChange={(e) => setSettings({ ...settings, risk_top_members_count: parseInt(e.target.value || '1', 10) })}
            data-testid="risk-top-members-count-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('tontineDetail.settings.highRiskCount')}</Label>
          <Input
            type="number"
            min="1"
            max="20"
            value={settings.risk_high_risk_count}
            onChange={(e) => setSettings({ ...settings, risk_high_risk_count: parseInt(e.target.value || '1', 10) })}
            data-testid="risk-high-risk-count-input"
          />
        </div>
      </div>

      <Button type="submit" className="w-full btn-accent" disabled={saving} data-testid="tontine-settings-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Settings className="w-4 h-4 mr-2" />}
        {t('tontineDetail.settings.saveButton')}
      </Button>
    </form>
  );
};

const EditTontineForm = ({ tontine, onSuccess }) => {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: tontine?.name || '',
    description: tontine?.description || '',
    contribution_amount: String(tontine?.contribution_amount || ''),
    frequency: tontine?.frequency || 'monthly',
    currency: tontine?.currency || 'XOF',
    max_members: String(tontine?.max_members || 10),
    rules: tontine?.rules || '',
  });

  useEffect(() => {
    if (!tontine) return;
    setFormData({
      name: tontine.name || '',
      description: tontine.description || '',
      contribution_amount: String(tontine.contribution_amount || ''),
      frequency: tontine.frequency || 'monthly',
      currency: tontine.currency || 'XOF',
      max_members: String(tontine.max_members || 10),
      rules: tontine.rules || '',
    });
  }, [tontine]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        contribution_amount: parseFloat(formData.contribution_amount),
        frequency: formData.frequency,
        currency: formData.currency,
        max_members: parseInt(formData.max_members, 10),
        rules: formData.rules,
      };
      await api.updateTontine(tontine.id, payload);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
      onInputCapture={clearLocalizedInvalid}
      onChangeCapture={clearLocalizedInvalid}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="edit-name">{t('tontine.name')} *</Label>
        <Input
          id="edit-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="edit-tontine-name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-description">{t('tontine.description')}</Label>
        <Textarea
          id="edit-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
          data-testid="edit-tontine-description"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('tontine.frequency')}</Label>
          <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
            <SelectTrigger data-testid="edit-tontine-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">{t('tontine.frequencies.weekly')}</SelectItem>
              <SelectItem value="biweekly">{t('tontine.frequencies.biweekly')}</SelectItem>
              <SelectItem value="monthly">{t('tontine.frequencies.monthly')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('tontine.currency')}</Label>
          <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
            <SelectTrigger data-testid="edit-tontine-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="XOF">XOF (FCFA)</SelectItem>
              <SelectItem value="CAD">CAD ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-amount">{t('tontine.contribution')} *</Label>
          <Input
            id="edit-amount"
            type="number"
            value={formData.contribution_amount}
            onChange={(e) => setFormData({ ...formData, contribution_amount: e.target.value })}
            required
            min="1"
            data-testid="edit-tontine-amount"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-max-members">{t('tontine.maxMembers')} *</Label>
          <Input
            id="edit-max-members"
            type="number"
            value={formData.max_members}
            onChange={(e) => setFormData({ ...formData, max_members: e.target.value })}
            required
            min="2"
            max="200"
            data-testid="edit-tontine-max-members"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-rules">{t('tontine.rules')}</Label>
        <Textarea
          id="edit-rules"
          value={formData.rules}
          onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
          rows={3}
          data-testid="edit-tontine-rules"
        />
      </div>

      <Button type="submit" className="w-full btn-accent" disabled={saving} data-testid="edit-tontine-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SquarePen className="w-4 h-4 mr-2" />}
        {t('common.save')}
      </Button>
    </form>
  );
};

export default TontineDetailPage;
